import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../hooks/useI18n';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import statisticsAPI, { Statistics } from '../services/statisticsAPI';
import { hasPermission, PERMISSIONS } from "../utils/authUtils";

import {
  TrendingUp,
  Users,
  BarChart3,
  FileText,
  Activity,
  Calendar,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Plus,
  Download,
  Eye,
  Building2,
  UserCheck,
  TrendingDown,
  DollarSign
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { selectedCompany } = useCompany();
  const navigate = useNavigate();
  const { t, isRTL } = useI18n();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Statistics | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStatistics();
  }, [selectedCompany.selectedCompanyId]);

  const loadStatistics = async () => {
    if (!selectedCompany.selectedCompanyId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await statisticsAPI.getStatistics(selectedCompany.selectedCompanyId);
      setStats(data);
    } catch (err: any) {
      console.error('Error loading statistics:', err);
      setError(err.response?.data?.message || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const companyCurrency = selectedCompany.selectedCompany?.currency || 'MAD';

  const statsCards = stats ? [
    {
      title: t('dashboard.totalRevenue'),
      value: `${stats.yearRevenue.toFixed(2)} ${companyCurrency}`,
      change: '',
      changeType: 'increase',
      icon: TrendingUp,
      color: 'bg-blue-500'
    },
    {
      title: t('dashboard.yearSpent'),
      value: `${stats.yearSpent.toFixed(2)} ${companyCurrency}`,
      change: '',
      changeType: 'decrease',
      icon: TrendingDown,
      color: 'bg-red-500'
    },
    {
      title: t('dashboard.totalInvoices'),
      value: stats.totalInvoices.toString(),
      change: '',
      changeType: 'increase',
      icon: FileText,
      color: 'bg-green-500'
    },
    {
      title: t('dashboard.totalClients'),
      value: stats.totalClients.toString(),
      change: `+${stats.newClientsThisMonth} ${t('dashboard.thisMonth')}`,
      changeType: 'increase',
      icon: Users,
      color: 'bg-purple-500'
    },
    {
      title: t('dashboard.pendingPayments'),
      value: `${stats.pendingPayments.toFixed(2)} ${companyCurrency}`,
      change: '',
      changeType: 'decrease',
      icon: DollarSign,
      color: 'bg-orange-500'
    },
    {
      title: t('dashboard.pendingToBePaid'),
      value: `${stats.pendingToBePaid.toFixed(2)} ${companyCurrency}`,
      change: '',
      changeType: 'decrease',
      icon: Activity,
      color: 'bg-yellow-500'
    }
  ] : [];

  const quickActions = [
    {
      title: t('dashboard.createCompany'),
      description: t('dashboard.createCompanyDesc'),
      icon: Building2,
      color: 'bg-blue-500',
      link: '/companies/create'
    },
    {
      title: t('dashboard.createClient'),
      description: t('dashboard.createClientDesc'),
      icon: UserCheck,
      color: 'bg-green-500',
      link: '/clients/create'
    },
    {
      title: t('dashboard.manageCompanies'),
      description: t('dashboard.manageCompaniesDesc'),
      icon: Building2,
      color: 'bg-purple-500',
      link: '/companies'
    },
    {
      title: t('dashboard.manageClients'),
      description: t('dashboard.manageClientsDesc'),
      icon: UserCheck,
      color: 'bg-orange-500',
      link: '/clients'
    },
    {
      title: t('dashboard.generateReport'),
      description: t('dashboard.generateReportDesc'),
      icon: FileText,
      color: 'bg-blue-500',
      action: () => { }
    },
    {
      title: t('dashboard.viewAnalytics'),
      description: t('dashboard.viewAnalyticsDesc'),
      icon: BarChart3,
      color: 'bg-green-500',
      action: () => { }
    },
    {
      title: t('dashboard.exportData'),
      description: t('dashboard.exportDataDesc'),
      icon: Download,
      color: 'bg-purple-500',
      action: () => { }
    },
    {
      title: t('dashboard.addUser'),
      description: t('dashboard.addUserDesc'),
      icon: Plus,
      color: 'bg-orange-500',
      action: () => { }
    }
  ];

  // Prepare chart data
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const revenueChartData = stats?.revenueByMonth?.map(item => ({
    month: monthNames[item.month - 1],
    revenue: item.revenue,
    cashRevenue: item.cashRevenue
  })) || [];

  const invoicePieData = stats ? [
    { name: t('dashboard.sales'), value: stats.sellInvoicesCount, color: '#10b981' },
    { name: t('dashboard.purchases'), value: stats.purchaseInvoicesCount, color: '#ef4444' }
  ] : [];

  const orderStatusData = stats ? [
    { name: t('dashboard.reservations'), value: stats.reservationOrdersCount, color: '#eab308' },
    { name: t('dashboard.confirmed'), value: stats.confirmedOrdersCount, color: '#3b82f6' },
    { name: t('dashboard.completed'), value: stats.completedOrdersCount, color: '#22c55e' },
    { name: t('dashboard.cancelled'), value: stats.cancelledOrdersCount, color: '#6b7280' }
  ] : [];

  const inventoryData = stats ? [
    { name: t('dashboard.healthyStock'), value: Math.max(0, stats.totalProducts - stats.lowStockProductsCount), color: '#22c55e' },
    { name: t('dashboard.lowStock'), value: stats.lowStockProductsCount, color: '#ef4444' }
  ] : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 spinner mx-auto mb-4"></div>
          <p className="text-secondary-600">{t('dashboard.loadingDashboard')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-secondary-50 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={`transition-all duration-300 ${isRTL ? 'lg:mr-64 lg:ml-0' : 'lg:ml-64 lg:mr-0'}`}>
        <TopNav onSidebarToggle={() => setIsSidebarOpen(true)} />

        <main className="p-6">
          {/* Welcome Section */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-secondary-900 mb-2">
              {t('dashboard.welcome', { username: user?.email?.split('@')[0] })}
            </h1>
            <p className="text-secondary-600">
              {t('dashboard.subtitle')}
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {statsCards.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="stat-card">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    {stat.change && (
                      <div className={`flex items-center space-x-1 text-sm ${stat.changeType === 'increase' ? 'text-success-600' : 'text-error-600'
                        }`}>
                        {stat.changeType === 'increase' ? (
                          <ArrowUpRight className="w-4 h-4" />
                        ) : (
                          <ArrowDownRight className="w-4 h-4" />
                        )}
                        <span>{stat.change}</span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-2xl font-bold text-secondary-900 mb-1">
                    {stat.value}
                  </h3>
                  <p className="text-secondary-600 text-sm">
                    {stat.title}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Company Selection Status */}
          {selectedCompany.selectedCompany && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-3`}>
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-blue-900">{t('dashboard.activeCompany')}</h3>
                  <p className="text-sm text-blue-700">{selectedCompany.selectedCompany.name}</p>
                </div>
              </div>
            </div>
          )}

          {/* Charts Section */}
          {stats && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Revenue Trend Chart */}
              <div className="card lg:col-span-2">
                <div className="card-header">
                  <h2 className="text-lg font-semibold text-secondary-900">{t('dashboard.revenueTrend')}</h2>
                  <p className="text-secondary-600 text-sm">{t('dashboard.last12Months')}</p>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={revenueChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="revenue"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        name={t('dashboard.revenue')}
                      />
                      <Line
                        type="monotone"
                        dataKey="cashRevenue"
                        stroke="#10b981"
                        strokeWidth={2}
                        name={t('dashboard.cashRevenue')}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Top Clients Chart */}
              <div className="card">
                <div className="card-header">
                  <h2 className="text-lg font-semibold text-secondary-900">{t('dashboard.topClients')}</h2>
                  <p className="text-secondary-600 text-sm">{t('dashboard.byRevenue')}</p>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.topClients} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="clientName" type="category" width={100} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="totalRevenue" name={t('dashboard.revenue')} fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Invoice Breakdown Pie Chart */}
              <div className="card">
                <div className="card-header">
                  <h2 className="text-lg font-semibold text-secondary-900">{t('dashboard.invoiceBreakdown')}</h2>
                  <p className="text-secondary-600 text-sm">{t('dashboard.byType')}</p>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={invoicePieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {invoicePieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {stats && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Order Status Chart */}
              <div className="card">
                <div className="card-header">
                  <h2 className="text-lg font-semibold text-secondary-900">{t('dashboard.orderStats')}</h2>
                  <p className="text-secondary-600 text-sm">{t('dashboard.byStatus')}</p>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={orderStatusData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="value" name={t('dashboard.orders')} fill="#8884d8">
                        {orderStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Inventory Health Chart */}
              {hasPermission(PERMISSIONS.STOCK_FEATURE) && (
                <div className="card">
                  <div className="card-header">
                    <h2 className="text-lg font-semibold text-secondary-900">{t('dashboard.inventoryHealth')}</h2>
                    <p className="text-secondary-600 text-sm">{t('dashboard.stockStatus')}</p>
                  </div>
                  <div className="card-body">
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={inventoryData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {inventoryData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>)}
            </div>
          )}

          {/* Content Grid
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
             <div>
              <div className="card">
                <div className="card-header">
                  <h2 className="text-lg font-semibold text-secondary-900">{t('dashboard.quickActions')}</h2>
                  <p className="text-secondary-600 text-sm">{t('dashboard.quickActionsSubtitle')}</p>
                </div>
                <div className="card-body">
                  <div className="space-y-3">
                    {quickActions.map((action, index) => {
                      const Icon = action.icon;
                      return (
                        <button
                          key={index}
                          onClick={() => action.link ? navigate(action.link) : (action.action && action.action())}
                          className={`w-full flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-3 p-3 rounded-lg hover:bg-secondary-50 transition-colors ${isRTL ? 'text-right' : 'text-left'}`}
                        >
                          <div className={`w-10 h-10 ${action.color} rounded-lg flex items-center justify-center`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-secondary-900">
                              {action.title}
                            </p>
                            <p className="text-xs text-secondary-500">
                              {action.description}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div> */}
        </main>
      </div>
    </div>
  );
};

export default DashboardPage;
